import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { TLead } from '@/models/leads.model';
import leadsService from '@/services/leads.service';
import { avatarColor } from '@/utils/call-center.utils';
import { Search, X } from 'lucide-react';
import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useFormContext } from 'react-hook-form';

const initialsOf = (name: string) =>
    name
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();

const LeadSelectorField = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [leads, setLeads] = useState<TLead[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const isSelectingRef = useRef(false);

    const { control, setValue, watch } = useFormContext();
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
    const itemsRef = useRef<Array<HTMLDivElement | null>>([]);

    const { leadId: selectedLeadId, firstName: selectedName, email: selectedEmail } = watch();

    useEffect(() => {
        if (isSelectingRef.current) {
            isSelectingRef.current = false;
            return;
        }

        const debounceTimeout = setTimeout(async () => {
            if (searchQuery.trim()) {
                setLoading(true);
                try {
                    const response = await leadsService.getAll({ query: searchQuery });
                    setLeads(response.data);
                    setShowDropdown(true);
                } catch {
                    setLeads([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setLeads([]);
                setShowDropdown(false);
            }
        }, 500);

        return () => clearTimeout(debounceTimeout);
    }, [searchQuery]);

    useEffect(() => {
        if (highlightedIndex !== null && itemsRef.current[highlightedIndex]) {
            itemsRef.current[highlightedIndex]!.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);

    const selectLead = (lead: TLead) => {
        isSelectingRef.current = true;
        setShowDropdown(false);
        setLeads([]);
        setSearchQuery(`${lead.firstName} ${lead.lastName || ''}`);
        setValue("leadId", lead.id, { shouldValidate: true });
        setValue("firstName", lead.firstName + (lead.lastName ? ` ${lead.lastName}` : ""));
        setValue("email", lead.email);
        setValue("phone", lead.phone);
        setHighlightedIndex(null);
    };

    // Presentation-only reset: mirrors the "Create Lead Manually" toggle reset
    // so the selected-lead card can be dismissed back to the search input.
    const clearSelectedLead = () => {
        setValue("leadId", undefined);
        setValue("firstName", "");
        setValue("email", "");
        setValue("phone", "");
        setSearchQuery("");
        setLeads([]);
        setShowDropdown(false);
        setHighlightedIndex(null);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!showDropdown) {
                if (leads.length) {
                    setShowDropdown(true);
                    setHighlightedIndex(0);
                }
            } else {
                setHighlightedIndex((prev) => (prev === null ? 0 : Math.min(prev + 1, leads.length - 1)));
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (showDropdown) {
                setHighlightedIndex((prev) => (prev === null ? Math.max(leads.length - 1, 0) : Math.max(prev - 1, 0)));
            }
        } else if (e.key === 'Enter') {
            if (showDropdown && highlightedIndex !== null) {
                e.preventDefault();
                selectLead(leads[highlightedIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    return (
        <FormField
            control={control}
            name="leadId"
            render={() => (
                <FormItem>
                    <FormLabel className="mb-2 block text-[10.5px] font-medium uppercase tracking-[0.12em] text-slate-500">
                        Add Lead to Deal <span className="text-accent-red">*</span>
                    </FormLabel>
                    <FormControl>
                        {selectedLeadId ? (
                            <div className="flex items-center gap-2.5 rounded-xl border border-white/[0.06] bg-gradient-to-br from-white/[0.025] to-white/[0.005] px-3 py-2">
                                <span
                                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-semibold"
                                    style={{ background: `${avatarColor(selectedName || "")}26`, color: avatarColor(selectedName || "") }}
                                >
                                    {initialsOf(selectedName || "")}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-[12.5px] font-medium text-slate-200">{selectedName}</p>
                                    {selectedEmail && <p className="truncate text-[10.5px] text-slate-500">{selectedEmail}</p>}
                                </div>
                                <button
                                    type="button"
                                    onClick={clearSelectedLead}
                                    aria-label="Clear selected lead"
                                    className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-white/[0.06] bg-white/[0.02] text-slate-400 transition hover:border-white/[0.14] hover:text-white"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <div className="flex h-10 items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 transition focus-within:border-brand-500/40">
                                    <Search className="h-4 w-4 shrink-0 text-slate-500" />
                                    <Input
                                        placeholder="Search for lead..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                        onFocus={() => searchQuery.trim() && leads.length > 0 && setShowDropdown(true)}
                                        onKeyDown={handleKeyDown}
                                        aria-expanded={showDropdown}
                                        className="h-auto flex-1 border-0 bg-transparent p-0 text-[12.5px] text-slate-200 outline-none placeholder:text-slate-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                                    />
                                </div>
                                {showDropdown && (
                                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-white/[0.08] bg-ink-800/95 text-slate-200 shadow-card backdrop-blur-xl custom-scrollbar">
                                        {loading ? (
                                            <div className="px-3 py-2 text-[12.5px] text-slate-500">Loading...</div>
                                        ) : leads.length > 0 ? (
                                            leads.map((lead, index) => (
                                                <div
                                                    ref={(el) => (itemsRef.current[index] = el)}
                                                    key={lead.id}
                                                    role="option"
                                                    aria-selected={highlightedIndex === index}
                                                    className={`cursor-pointer px-3 py-2 text-[12.5px] transition ${highlightedIndex === index ? 'bg-white/[0.05]' : 'hover:bg-white/[0.05]'}`}
                                                    onMouseEnter={() => setHighlightedIndex(index)}
                                                    onClick={() => selectLead(lead)}
                                                >
                                                    <div className="truncate font-medium text-slate-200">{lead.firstName} {lead.lastName}</div>
                                                    <div className="truncate text-[10.5px] text-slate-500">{lead.email}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-[12.5px] text-slate-500">No leads found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </FormControl>
                    <FormMessage className="text-[10.5px] text-accent-red" />
                </FormItem>
            )}
        />
    );
};

export default LeadSelectorField;
